/**
 * Who can be added to a support group (owner decision #3). Pure, no Firebase.
 *
 *  - ORG group: that organization's beneficiaries only — the manager screens'
 *    query (getOrganizationMembers), deduplicated and matched by name or phone
 *    here, the way mobile filterBeneficiaries does.
 *  - PERSONAL group: an exact Saudi mobile number, looked up on the server by
 *    the searchUsersForGroup callable (no client reads `users` to search).
 */
import { normalizePhone } from '@/lib/accountDeletion/identity';

export type GroupAddMode = 'org' | 'personal';

/** Org group → pick from the organization's beneficiaries; personal → by phone. */
export function groupAddMode(group: { organization?: string | null }): GroupAddMode {
  return group.organization ? 'org' : 'personal';
}

/**
 * The local 05XXXXXXXX form of a typed Saudi mobile number, or '' while the
 * input is not a full one (05…, +9665…, 009665…, Arabic-Indic digits).
 */
export function personalSearchPhone(raw: string): string {
  return normalizePhone(raw);
}

export interface CandidatePerson {
  id: string;
  userId?: string;
  firstNameAr?: string;
  lastNameAr?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  name?: string;
  phone?: string;
  phoneNumber?: string;
}

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC = '۰۱۲۳۴۵۶۷۸۹';

function westernDigits(s: string): string {
  let out = '';
  for (const ch of s) {
    const a = ARABIC_INDIC.indexOf(ch);
    if (a >= 0) { out += String(a); continue; }
    const e = EASTERN_ARABIC.indexOf(ch);
    out += e >= 0 ? String(e) : ch;
  }
  return out;
}

/** The person's uid: the membership row's userId, else the user doc id. */
export function candidateUid(p: CandidatePerson): string {
  return p.userId || p.id;
}

/**
 * One entry per person (getOrganizationMembers returns one per membership
 * row, so a beneficiary who is also an examiner appears twice), minus anyone
 * already in the group.
 */
export function orgCandidates<T extends CandidatePerson>(
  people: ReadonlyArray<T>,
  existingMemberIds: Iterable<string>,
): T[] {
  const existing = new Set(existingMemberIds);
  const seen = new Set<string>();
  const out: T[] = [];
  for (const p of people) {
    const uid = candidateUid(p);
    if (!uid || seen.has(uid) || existing.has(uid)) continue;
    seen.add(uid);
    out.push(p);
  }
  return out;
}

/**
 * Name (every word, Arabic or English fields) or phone match. A full Saudi
 * number matches in any spelling; otherwise 3+ digits match inside the phone.
 */
export function matchesCandidateQuery(p: CandidatePerson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const haystack = [
    p.firstNameAr, p.lastNameAr, p.firstNameEn, p.lastNameEn,
    p.firstName, p.lastName, p.displayName, p.name,
  ].filter(Boolean).join(' ').toLowerCase();
  if (q.split(/\s+/).every((w) => haystack.includes(w))) return true;

  const phone = p.phone || p.phoneNumber || '';
  const queryLocal = normalizePhone(q);
  const storedLocal = normalizePhone(phone);
  if (queryLocal && storedLocal) return queryLocal === storedLocal;
  const tail = (s: string) => westernDigits(s).replace(/\D/g, '').replace(/^(00966|966|0)/, '');
  const qt = tail(q);
  return qt.length >= 3 && tail(phone).includes(qt);
}

/** At most `limit` matches for the typed query; nothing for an empty one. */
export function searchOrgCandidates<T extends CandidatePerson>(
  candidates: ReadonlyArray<T>,
  query: string,
  limit = 20,
): T[] {
  if (!query.trim()) return [];
  return candidates.filter((p) => matchesCandidateQuery(p, query)).slice(0, limit);
}
